import cuid from "cuid";
import { get, set } from "lodash";
import { RELATIONSHIP } from "utils/constants";

const ensureId = (id) => id || cuid();

const defaultResolveId = (_key, existing) => ensureId(existing);

const buildProjectPatch = (
  target,
  value,
  project,
  itemIndex,
  resolveId = defaultResolveId,
) => {
  const patch = { id: project.id };

  if (target.entity === "user") {
    patch.contact = {
      id: resolveId("contact", get(project, "contact.id")),
      user: { id: resolveId("contact.user", get(project, "contact.user.id")) },
    };
    set(patch, `contact.user.${target.property}`, value);
    return patch;
  }

  if (target.entity === "contact") {
    patch.contact = { id: resolveId("contact", get(project, "contact.id")) };
    set(patch, `contact.${target.property}`, value);
    return patch;
  }

  if (target.entity === "telecom") {
    const existing = (get(project, "contact.telecoms") || []).find(
      (t) => t.system === target.filterValue,
    );
    const telecom = existing
      ? { ...existing, [target.property]: value }
      : {
          id: resolveId(
            `contact.telecoms.${target.filterValue}`,
            null,
          ),
          system: target.filterValue,
          [target.property]: value,
        };
    patch.contact = {
      id: resolveId("contact", get(project, "contact.id")),
      telecoms: [
        ...(get(project, "contact.telecoms") || []).filter(
          (t) => t.system !== target.filterValue,
        ),
        telecom,
      ],
    };
    return patch;
  }

  if (target.entity === "contract") {
    patch.contract = {
      id: resolveId("contract", get(project, "contract.id")),
    };
    set(patch, `contract.${target.property}`, value);
    return patch;
  }

  if (target.entity === "project") {
    set(patch, target.property, value);
    return patch;
  }

  if (target.entity === "spouse" || target.entity === "children") {
    const relationship =
      target.entity === "spouse" ? RELATIONSHIP.SPOUSE : RELATIONSHIP.CHILD;
    const all = get(project, "contact.relatedPersons") || [];
    const others = all.filter((p) => p.relationship !== relationship);
    const same = all.filter((p) => p.relationship === relationship);
    const updated = same.map((p, i) => {
      const isTarget = target.entity === "spouse" ? i === 0 : i === itemIndex;
      if (!isTarget) return p;
      const merged = {
        ...p,
        identity: {
          ...(p.identity || {}),
          id: ensureId(get(p, "identity.id")),
          user: {
            ...(get(p, "identity.user") || {}),
            id: ensureId(get(p, "identity.user.id")),
          },
        },
      };
      set(merged, target.property, value);
      return merged;
    });
    patch.contact = {
      id: ensureId(get(project, "contact.id")),
      relatedPersons: [...others, ...updated],
    };
    return patch;
  }

  return patch;
};

const buildSubscriptionPatch = (target, value, subscription) => {
  const patch = { id: subscription.id };

  if (target.entity === "subscription") {
    set(patch, target.property, value);
    return patch;
  }

  if (target.entity === "payment") {
    patch.payment = { id: ensureId(get(subscription, "payment.id")) };
    if (target.property.startsWith("payer.owner.")) {
      patch.payment.payer = {
        id: ensureId(get(subscription, "payment.payer.id")),
        owner: { id: ensureId(get(subscription, "payment.payer.owner.id")) },
      };
    } else if (target.property.startsWith("payer.")) {
      patch.payment.payer = {
        id: ensureId(get(subscription, "payment.payer.id")),
      };
    } else if (target.property.startsWith("receiver.owner.")) {
      patch.payment.receiver = {
        id: ensureId(get(subscription, "payment.receiver.id")),
        owner: { id: ensureId(get(subscription, "payment.receiver.owner.id")) },
      };
    } else if (target.property.startsWith("receiver.")) {
      patch.payment.receiver = {
        id: ensureId(get(subscription, "payment.receiver.id")),
      };
    }
    set(patch.payment, target.property, value);
    return patch;
  }

  return patch;
};

export const buildPatch = (
  target,
  value,
  entity,
  entityType,
  itemIndex,
  resolveId,
) => {
  if (entityType === "project") {
    return buildProjectPatch(target, value, entity, itemIndex, resolveId);
  }
  if (entityType === "subscription") {
    return buildSubscriptionPatch(target, value, entity);
  }
  return { id: entity.id };
};

export const buildAddRelatedPatch = (project, relationship) => ({
  id: project.id,
  contact: {
    id: ensureId(get(project, "contact.id")),
    relatedPersons: [
      ...(get(project, "contact.relatedPersons") || []),
      {
        id: cuid(),
        relationship,
        identity: { id: cuid(), user: { id: cuid() } },
      },
    ],
  },
});

export const buildRemoveRelatedPatch = (project, personId) => ({
  id: project.id,
  contact: {
    id: ensureId(get(project, "contact.id")),
    relatedPersons: (get(project, "contact.relatedPersons") || []).filter(
      (p) => p.id !== personId,
    ),
  },
});
